/**
 * Native currency v1 — simple client-side conversion (#chunk3).
 *
 * The calculator's displayed price is converted CLIENT-SIDE using Shopify's own
 * `window.Shopify.currency` (the same {active, rate} pair Shopify's theme
 * currency selector maintains). No backend rates, no Nova `baCurr` layer, no
 * contextualPricing, no cache, no rounding inference — measured accurate to
 * ~0.05% vs Shopify checkout, so a single multiply + Intl format is enough.
 *
 * This is display-only: charging (variant/cart-transform) stays in the shop's
 * base currency and is untouched. Scope is the MAIN price ticker only
 * (pricing/ticker.ts). Follow-ups intentionally NOT converted in v1:
 * calculation sub-elements, the cart page, and the merchant Prefix/Suffix
 * sibling nodes emitted by elements/price-marker.ts.
 */
import { appState } from "../state/app-state";

export interface ShopifyCurrency {
  active: string;
  rate: number;
}

/**
 * Reads the theme's `window.Shopify.currency` and coerces `rate` to a float
 * (Shopify serialises it as a string, e.g. "3.7771"). Returns null when the
 * object is absent or the rate cannot be parsed to a number.
 */
export function GetShopifyCurrency(): ShopifyCurrency | null {
  const currency = window.Shopify?.currency;
  if (!currency || typeof currency.active !== "string") return null;
  const rate = parseFloat(currency.rate);
  if (!Number.isFinite(rate)) return null;
  return { active: currency.active, rate };
}

/**
 * True only when the merchant enabled native currency, the theme exposes a
 * usable Shopify.currency, and the shopper is viewing a NON-base currency
 * (rate !== 1 → base currency, leave the existing display untouched).
 */
export function IsNativeCurrencyActive(): boolean {
  if (appState.nativeCurrencyEnabled !== true) return false;
  const currency = GetShopifyCurrency();
  if (!currency) return false;
  return Number.isFinite(currency.rate) && currency.rate > 0 && currency.rate !== 1;
}

/** Converts a base-currency amount to the active currency (unrounded). */
export function ConvertToActive(baseAmount: number): number {
  const currency = GetShopifyCurrency();
  const rate = currency ? currency.rate : 1;
  return baseAmount * rate;
}

/**
 * Formats an already-converted amount with the active currency using the
 * browser's Intl (handles symbol, grouping separators and the currency's own
 * decimal count — e.g. JPY 0-decimals). Falls back to `amount.toFixed(2) +
 * " " + code` when the currency code is not one Intl recognises.
 */
export function FormatActive(amount: number): string {
  const currency = GetShopifyCurrency();
  const active = currency ? currency.active : "";
  try {
    return new Intl.NumberFormat(navigator.language || "en", {
      style: "currency",
      currency: active,
    }).format(amount);
  } catch {
    return amount.toFixed(2) + " " + active;
  }
}
